import { Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../component/confirmDialog/ConfirmDialog';
import { ToolForm } from '../../component/tools/ToolForm';
import { ToolsList } from '../../component/tools/ToolsList';
import useBooleanKeys from '../../hooks/useBooleanKeys';
import useSearchParams from '../../hooks/useSearchParams';
import useTablePagination from '../../hooks/useTablePagination';
import { clearAddAnother, getProductToolsById, listItemRequestNewAll, listProductTools, setLastFormData } from '../../redux/slices/tools/toolsSlice';
import { getUserAbilities } from '../../redux/slices/users/usersSlice';
import { useDispatch } from '../../redux/store';

export const Tools: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setURLParams] = useSearchParams();
  const [redirectTo, setRedirectTo] = useState<string | undefined>();
  const [addAnOther, setAddAnOther] = useState(false);
  const paramsToUse = useMemo(
    () => Object.keys(params).reduce((partParams, key) => ({ ...partParams, [key]: params[key] }), { active: 'true', productTypeId: '1,3,4,6' }),
    [params],
  );
  const productTools = useSelector(getProductToolsById(paramsToUse));
  const [modals, toggleModal] = useBooleanKeys({ showAddProductItem: params.new !== undefined, showAddAnother: false });
  const [pagination, paginationToGet, paginationControl, paginationToGetAll] = useTablePagination({ paginationDirty: productTools.pagination });
  const productToolsExport = useSelector(getProductToolsById({ ...paramsToUse, ...paginationToGetAll }));
  const abilities = useSelector(getUserAbilities);
  const wasAdded = useSelector(listItemRequestNewAll);
  const wasAddedRef = useRef(wasAdded);
  wasAddedRef.current = wasAdded;

  useEffect(() => {
    dispatch(
      listProductTools({
        ...paramsToUse,
        ...paginationToGet,
      }),
    );
  }, [dispatch, paramsToUse, paginationToGet]);

  useEffect(() => {
    if (params.new) {
      dispatch(clearAddAnother());
    }
  }, [dispatch, params.new]);

  const getExportData = useCallback(async () => {
    await dispatch(
      listProductTools({
        ...paramsToUse,
        ...paginationToGetAll,
      }),
    );
  }, [dispatch, paramsToUse, paginationToGetAll]);

  const handleCloseAddProductItem = async (options?: { redirect?: string; edit?: boolean }) => {
    setAddAnOther(false);
    toggleModal('showAddProductItem', false);
    setURLParams({ new: undefined });

    if (options && !options?.edit) {
      if (wasAddedRef.current?.length > 1 && (abilities.can('Container') || abilities.can('Roll-Off'))) {
        setTimeout(() => {
          setAddAnOther(true);
          toggleModal('showAddProductItem', true);
        }, 300);
      } else {
        toggleModal('showAddAnother', true);
        setRedirectTo(options.redirect);
      }

      dispatch(
        listProductTools({
          ...paramsToUse,
          ...paginationToGet,
        }),
      );
    } else if (wasAddedRef.current?.length > 1 && (abilities.can('Container') || abilities.can('Roll-Off'))) {
      navigate('/tools/batch-enrollment');
    } else if (options?.redirect) {
      navigate(options.redirect);
    }
  };

  const handleAddAnother = () => {
    setAddAnOther(true);
    toggleModal('showAddAnother', false);
    toggleModal('showAddProductItem', true);
  };

  const handleCloseAnother = () => {
    toggleModal('showAddAnother', false);
    dispatch(setLastFormData(undefined));
    if (redirectTo) {
      navigate(redirectTo);
    }
  };

  const handleAddProductItem = () => {
    dispatch(clearAddAnother());
    toggleModal('showAddProductItem', true);
  };

  return (
    <>
      <ToolsList
        items={productTools}
        itemsExport={productToolsExport}
        {...(abilities.can('create') && {
          headerAddon: (
            <Button color="primary" variant="contained" onClick={handleAddProductItem}>
              New
            </Button>
          ),
        })}
        withFilters
        paginationControl={paginationControl}
        pagination={pagination}
        withAssignedTo
        withActive
        withProduct
        withManufacturer
        getExportData={getExportData}
      />
      <ToolForm open={modals.showAddProductItem} onClose={handleCloseAddProductItem} type="tools" addAnOther={addAnOther} nextCount={wasAdded.length + 1} />
      <ConfirmDialog
        open={modals.showAddAnother}
        onClose={handleCloseAnother}
        title="Add another?"
        onConfirm={handleAddAnother}
        cancelLabel="No"
        confirmLabel="Yes"
        confirmColor="success"
        dialogProps={{
          fullWidth: true,
          maxWidth: 'xs',
        }}
      />
    </>
  );
};
